import classNames from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import React, { type FC } from 'react'
import fallbackImage from './assets/fallback.png'
import { StoreAvailabilityInfo } from './partials/store-availability/StoreAvailabilityInfo'
import { StoreCardSkeleton } from './partials/store-card-skeleton/StoreCardSkeleton'
import { StoreDeliveryFeeInfo } from './partials/store-delivery-fee/StoreDeliveryFee'
import { StorePromotionTag } from './partials/store-promotions/StorePromotion'
import { StoreRatings } from './partials/store-ratings/StoreRatings'
import styles from './StoreCard.module.scss'
import { useDeliveryAddress } from '@/delivery-address/client'
import { useLocalisation } from '@/tools/i18n/client'
import { getImageUrl } from '@/tools/image-service/client'
import { Avatar, AvatarSize, Card, Tag, TagStyle } from '@/tools/ui'

interface Props {
  showSkeleton?: boolean
  avatarImage?: string | null
  bgImage: string
  storePath: string
  title: string
  sponsoredStoreDescription?: string | null
  isTopRated?: boolean
  isNew: boolean
  eta?: string
  isPrime?: boolean
  promotions: {
    isPrime: boolean
    label: string
  }[]
  newStoreIcon?: string
  ratings: { score: string; icon: string; votes: string }
  availabilityMessage?: string | null
  isStoreClosed: boolean
  storeHasSchedule: boolean
  shouldShowPrimeFee: boolean
  //temporary solutiion since isDiscountedDeliveryFee seems to
  // be missing in many stores (needs to be updated in entities.ts once fixed)
  hasSpecialFee?: boolean | undefined
  deliveryFee?: {
    baseFee: string
    effectiveFee?: string
    isPrimeDeliveryFee?: boolean
    isSurged?: boolean
    isFreeDeliveryFee?: boolean
    isDiscountedDeliveryFee?: boolean
  }
  prefetch?: boolean | null
}

export const StoreCard: FC<Props> = ({
  showSkeleton,
  storePath,
  bgImage,
  avatarImage,
  title,
  sponsoredStoreDescription,
  isTopRated = false,
  isNew,
  eta,
  promotions,
  storeHasSchedule,
  isStoreClosed,
  availabilityMessage,
  shouldShowPrimeFee,
  hasSpecialFee = false,
  deliveryFee,
  ratings,
  prefetch = null,
}) => {
  const { t } = useLocalisation()
  const { deliveryAddress } = useDeliveryAddress()

  const topRatedIcon = getImageUrl({
    source: 'dh:customer-assets-glovo/feed/carousels/top_performers_light.svg',
  })

  const isStoreOpen = !isStoreClosed && !storeHasSchedule

  return showSkeleton ? (
    <StoreCardSkeleton />
  ) : (
    <Link className={styles.wrapper} href={storePath} prefetch={prefetch}>
      <Card className={styles.card}>
        {bgImage && (
          <Image
            fill
            alt={title}
            blurDataURL={fallbackImage.src}
            className={styles.backgroundImage}
            placeholder="blur"
            src={bgImage}
          />
        )}
        <div className={styles.topSection}>
          <div className={styles.promotion}>
            {promotions &&
              promotions
                .slice(0, 2)
                .map((promotion, i) => (
                  <StorePromotionTag key={i} isPrime={promotion.isPrime} label={promotion.label} />
                ))}
          </div>
          <div className={styles.cardBottom}>
            {avatarImage && (
              <Avatar
                className={styles.logo}
                imgAlt={title}
                imgSrc={avatarImage}
                size={AvatarSize.medium}
              />
            )}
            {sponsoredStoreDescription && (
              <Tag label={sponsoredStoreDescription ?? ''} style={TagStyle.sponsored} />
            )}
          </div>
        </div>
      </Card>
      <div className={styles.bottomSection}>
        <h3 className={classNames('pintxo-typography-body1-emphasis', styles.title)}>
          {isTopRated && (
            <Image alt={t('store-wall.top-rated')} height={18} src={topRatedIcon} width={18} />
          )}
          {title}
        </h3>
        <div className={styles.details}>
          <StoreRatings isNew={isNew} ratings={ratings} />
          {isStoreOpen ? (
            <>
              {deliveryAddress?.text && eta && (
                <div className={styles.eta}>
                  <p className={classNames(styles.eta, 'pintxo-typography-caption')}>{eta}</p>
                </div>
              )}
              {deliveryFee && (
                <StoreDeliveryFeeInfo
                  deliveryFee={deliveryFee}
                  hasSpecialFee={hasSpecialFee}
                  shouldShowPrimeFee={shouldShowPrimeFee}
                />
              )}
            </>
          ) : (
            <StoreAvailabilityInfo
              availabilityMessage={availabilityMessage ?? ''}
              isStoreClosed={isStoreClosed}
              storeHasSchedule={storeHasSchedule}
            />
          )}
        </div>
      </div>
    </Link>
  )
}
